import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import {
    Box,
    Paper,
    Grid,
    Typography,
    Button,
    Tooltip,
    LinearProgress,
    Divider,
    CircularProgress,
} from "@material-ui/core";
import GetAppIcon from "@material-ui/icons/GetApp";
import GridOnIcon from "@material-ui/icons/GridOn";
import LinkOffIcon from "@material-ui/icons/LinkOff";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import Alert from "@material-ui/lab/Alert";
import { useDispatch, useSelector } from "react-redux";
import { useParams, Link as RouterLink } from "react-router-dom";
import { fetchWorkspaceStats } from "../../actions/stats";
import { selectLastCrawlId } from "../../selectors/crawlIdSelector";
import { CountryFlag } from "../Workspace/CountryFlag";

/* ---------- styled ---------- */

const Page = styled(Box)`
    margin-top: 12px;
`;

const Header = styled(Paper)`
    padding: 16px;
    border-radius: 12px;
    background: #ffffff;
    border: 1px solid #e5e7eb;
`;

const TitleBlock = styled.div`
    display: grid;
    grid-template-columns: 24px auto;
    grid-column-gap: 8px;
    align-items: center;
`;

const FlagInline = styled.span`
    display: inline-flex;
    align-items: center;
    & img,
    & svg {
        height: 18px;
        width: auto;
        border-radius: 2px;
    }
`;

const NameText = styled(Typography)`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const DomainText = styled(Typography)`
    min-width: 0 !important;
    line-height: 1.25;
`;

const DomainLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  color: inherit;
  text-decoration: none;
`;

const DomainEllipsis = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Card = styled(Paper)`
  padding: 16px;
  border-radius: 12px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border: 1px solid #e5e7eb;
  background: #ffffff;
`;

const CardHead = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
`;

const Actions = styled(Box)`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const Subtle = styled.span`
  color: #5f6368;
`;

/* ---------- helpers ---------- */

function pickFilename(contentDisposition, fallback) {
    try {
        if (!contentDisposition) return fallback;
        const match = contentDisposition
            .split(";")
            .map((s) => s.trim())
            .find((s) => s.toLowerCase().startsWith("filename="));
        if (!match) return fallback;
        return match.split("=")[1].replace(/^"+|"+$/g, "");
    } catch {
        return fallback;
    }
}

async function downloadFile({ url, fallbackName, setLoading, setError }) {
    setLoading(true);
    setError(null);
    try {
        const res = await fetch(url, { method: "GET", credentials: "include" });
        if (!res.ok) {
            const text = await res.text().catch(() => "");
            throw new Error(text || `Request failed with ${res.status}`);
        }
        const blob = await res.blob();
        const cd = res.headers.get("content-disposition");
        const filename = pickFilename(cd, fallbackName);

        const href = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = href;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(href);
    } catch (e) {
        setError(e.message || "Something went wrong.");
    } finally {
        setLoading(false);
    }
}

function safeHref(url) {
    if (!url) return null;
    return /^https?:\/\//i.test(url) ? url : `https://${url}`;
}
function displayHost(url) {
    try {
        const href = safeHref(url);
        if (!href) return null;
        const u = new URL(href);
        return u.host.replace(/^www\./, "");
    } catch {
        return null;
    }
}

/* ---------- component ---------- */

export default function ExportsPage() {
    const dispatch = useDispatch();

    // URL param (workspace-scoped route expected, e.g. /ws/:workspaceNumber/exports)
    const { workspaceNumber: wsParam } = useParams();
    const wsNumberFromParam = Number(wsParam);
    const hasParamWs = Number.isFinite(wsNumberFromParam);

    // Redux workspace state
    const cur = useSelector((s) => s.workspaces?.current);
    const currentNumber = Number(cur?.seqNumber);
    const hasCurrentWs = Number.isFinite(currentNumber);

    // Prefer the URL param; otherwise fall back to the current workspace
    const activeWsNumber = hasParamWs ? wsNumberFromParam : hasCurrentWs ? currentNumber : null;
    const hasActiveWorkspace = Number.isFinite(activeWsNumber);

    // hydrate stats (needed for selectLastCrawlId), mirroring DashboardSimple
    useEffect(() => {
        if (hasParamWs) {
            dispatch(fetchWorkspaceStats(wsParam));
        }
    }, [dispatch, hasParamWs, wsParam]);

    const lastCrawlId = useSelector(selectLastCrawlId);

    const excelUrl = useMemo(
        () =>
            hasActiveWorkspace
                ? `/api/monitored-uris/download?workspaceNumber=${activeWsNumber}`
                : null,
        [hasActiveWorkspace, activeWsNumber]
    );

    const brokenLinksUrl = useMemo(
        () =>
            hasActiveWorkspace && lastCrawlId
                ? `/archive-api/links/export/broken?websiteCrawlId=${lastCrawlId}`
                : null,
        [hasActiveWorkspace, lastCrawlId]
    );

    const [excelLoading, setExcelLoading] = useState(false);
    const [brokenLoading, setBrokenLoading] = useState(false);
    const [excelError, setExcelError] = useState(null);
    const [brokenError, setBrokenError] = useState(null);

    const anyLoading = excelLoading || brokenLoading;

    const wsName = cur?.name || `Workspace ${activeWsNumber ?? ""}`.trim();
    const domain = displayHost(cur?.websiteUrl);
    const href = safeHref(cur?.websiteUrl);

    return (
        <Page>
            {anyLoading && <LinearProgress />}

            {/* Header — mirrors Workspace card/title pattern (name + domain link). No pills. */}
            <Header elevation={0}>
                <Box mb={0.5}>
                    <TitleBlock>
                        <FlagInline>
                            {cur?.countryCode ? <CountryFlag countryCode={cur.countryCode} /> : null}
                        </FlagInline>
                        <NameText variant="h6" title={wsName}>
                            {wsName}
                        </NameText>
                    </TitleBlock>
                </Box>

                {domain ? (
                    <DomainText variant="body2" color="textSecondary">
                        <DomainLink href={href} target="_blank" rel="noopener noreferrer" title={href}>
                            <DomainEllipsis>{domain}</DomainEllipsis>
                            <OpenInNewIcon style={{ fontSize: 18 }} />
                        </DomainLink>
                    </DomainText>
                ) : (
                    <Typography variant="body2" color="textSecondary">No website</Typography>
                )}

                <Box mt={1.2}>
                    <Typography variant="body2" color="textSecondary">
                        Download files for Excel or your reporting workflow. A loader shows while we prepare each file
                        (typically 5–30s).
                    </Typography>
                </Box>
            </Header>

            {!hasActiveWorkspace && (
                <Box mt={2}>
                    <Alert severity="info" icon={<InfoOutlinedIcon />}>
                        Select a workspace first — exports use your current selection.
                    </Alert>
                </Box>
            )}

            <Box mt={2}>
                <Grid container spacing={2}>
                    {/* Monitored URLs (Excel) */}
                    <Grid item xs={12} md={6}>
                        <Card elevation={0}>
                            <Box>
                                <CardHead>
                                    <GridOnIcon />
                                    <Typography variant="subtitle2">Monitored URLs (Excel)</Typography>
                                </CardHead>
                                <Typography variant="body2" color="textSecondary">
                                    Spreadsheet of pages you track. Columns include
                                    <Subtle> URL, HTTP status, Title, Template, Last seen</Subtle>. Great for filtering, pivoting
                                    and sharing.
                                </Typography>
                            </Box>

                            <Box mt={2}>
                                <Actions>
                                    <Tooltip
                                        title={
                                            hasActiveWorkspace
                                                ? excelLoading
                                                    ? "Preparing your file…"
                                                    : ""
                                                : "Select a workspace first"
                                        }
                                    >
                    <span>
                      <Button
                          variant="contained"
                          color="primary"
                          startIcon={<GetAppIcon />}
                          disabled={!excelUrl || excelLoading}
                          onClick={() =>
                              downloadFile({
                                  url: excelUrl,
                                  fallbackName: `monitored-urls-${activeWsNumber ?? "workspace"}.xlsx`,
                                  setLoading: setExcelLoading,
                                  setError: setExcelError,
                              })
                          }
                      >
                        {excelLoading ? (
                            <>
                                Exporting…
                                <CircularProgress size={18} style={{ marginLeft: 8 }} />
                            </>
                        ) : (
                            "Export Excel (.xlsx)"
                        )}
                      </Button>
                    </span>
                                    </Tooltip>
                                </Actions>

                                {excelError && (
                                    <Box mt={1}>
                                        <Alert severity="error">{excelError}</Alert>
                                    </Box>
                                )}
                            </Box>
                        </Card>
                    </Grid>

                    {/* Broken links (CSV) */}
                    <Grid item xs={12} md={6}>
                        <Card elevation={0}>
                            <Box>
                                <CardHead>
                                    <LinkOffIcon />
                                    <Typography variant="subtitle2">Broken links (CSV)</Typography>
                                </CardHead>
                                <Typography variant="body2" color="textSecondary">
                                    CSV of broken links from your <b>most recent crawl</b>. Includes
                                    <Subtle> Source URL, Anchor text, Target URL, Status</Subtle>. Ideal for briefing dev/QA and
                                    tracking fixes.
                                </Typography>

                                <Box mt={1}>
                                    <Divider />
                                </Box>

                                {!lastCrawlId && (
                                    <Box mt={1}>
                                        <Typography variant="caption" color="textSecondary">
                                            No recent crawl found.{" "}
                                            {hasActiveWorkspace && (
                                                <Button
                                                    component={RouterLink}
                                                    to={`/ws/${activeWsNumber}/broken-links`}
                                                    size="small"
                                                    color="primary"
                                                >
                                                    Open Broken Links
                                                </Button>
                                            )}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>

                            <Box mt={2}>
                                <Actions>
                                    <Tooltip
                                        title={
                                            !hasActiveWorkspace
                                                ? "Select a workspace first"
                                                : !lastCrawlId
                                                    ? "Run or open a crawl to enable this export"
                                                    : brokenLoading
                                                        ? "Preparing your file…"
                                                        : ""
                                        }
                                    >
                    <span>
                      <Button
                          variant="contained"
                          color="primary"
                          startIcon={<GetAppIcon />}
                          disabled={!brokenLinksUrl || brokenLoading}
                          onClick={() =>
                              downloadFile({
                                  url: brokenLinksUrl,
                                  fallbackName: `broken-links-${activeWsNumber ?? "workspace"}.csv`,
                                  setLoading: setBrokenLoading,
                                  setError: setBrokenError,
                              })
                          }
                      >
                        {brokenLoading ? (
                            <>
                                Exporting…
                                <CircularProgress size={18} style={{ marginLeft: 8 }} />
                            </>
                        ) : (
                            "Export CSV"
                        )}
                      </Button>
                    </span>
                                    </Tooltip>
                                </Actions>

                                {brokenError && (
                                    <Box mt={1}>
                                        <Alert severity="error">{brokenError}</Alert>
                                    </Box>
                                )}
                            </Box>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            <Box mt={2}>
                <Alert severity="info" icon={<InfoOutlinedIcon />}>
                    Large exports can take up to <b>30 seconds</b> to begin while we prepare your file. Keep this tab open — your
                    download will start automatically.
                </Alert>
            </Box>
        </Page>
    );
}
